import Icon from "@components/Icon";
import { colours, getColours } from "@helpers";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import type {
	IModuleTestimonialItem,
	IReviewModuleFields,
} from "@types-contentful";
import React from "react";
import Slider from "react-slick";
import styled from "styled-components";
import CTAButton from "./CTAButton";
import TestimonialItem from "./TestimonialItem";

type ContainerProps = {
	resetModuleMarginBottom: string;
	Colour: string;
	padding: boolean;
};

const Container = styled.div<ContainerProps>`
  overflow: hidden;
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => getColours(props.Colour)};
  padding: ${({ padding }) =>
		padding === true ? "40px 32px 100px 32px" : "40px 32px"};

  a {
    margin-top: 78px;
  }
  h2 {
    margin-bottom: 16px;
    text-align: center;
  }
  p {
    margin-bottom: 32px;
  }
  .slick-track {
    display: flex;
    justify-content: center;
  }

  .ft-slick__dots--custom {
    height: 4px;
    width: 14px;
    border-radius: 12px;
    position: relative;
  }

  .slick-dots li {
    width: 24px;
    margin: 0 2px;
    transition: width 0.3s ease-in-out;
    background-color: #d1c0c9;
    border-radius: 12px;
    height: 4px;
  }

  .slick-dots .slick-active {
    width: 56px;
    background-color: ${colours.MIDNIGHT_BLUE};
    height: 4px;
    border-radius: 12px;
    position: relative;
  }
  .slick-dots {
    width: 100%;
    top: 394px;
    @media (min-width: 1024px) {
      left: 46%;
      width: 88px;
    }
  }

  .slick-dots .slick-active .ft-slick__dots--custom {
    width: 56px;
    top: 0px;
    overflow: hidden;
  }
`;
const ReviewBlockContainer = styled.div<{ backgroundColour: string }>`
  .slick-list {
    padding: 0px;
  }
  .slick-slider {
    width: 400px;
    @media (min-width: 1024px) {
      width: 1008px;
    }
    @media (min-width: 1190px) {
      width: 1152px;
    }
  }
  .slick-slide {
    border-radius: 10px;
    width: 304px !important;
    height: 368px !important;
    background-color: ${(props) => getColours(props.backgroundColour)};
    border-radius: 12px;
    margin: 0 8px;
    padding: 0 40px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    @media (min-width: 1024px) {
      width: 320px !important;
      height: 368px !important;
    }
    @media (min-width: 1190px) {
      width: 368px !important;
      height: 368px !important;
    }
  }
`;

const ArrowRightSpan = styled.span`
  width: 32px;
  height: 32px;
  margin-top: 225px;
  margin-right: 90px;

  @media (min-width: 1024px) {
    margin-right: 450px;
    margin-top: 227px;
  }
`;

const ArrowLeftSpan = styled.span`
  width: 32px;
  height: 32px;
  margin-top: 225px;
  margin-left: 85px;
  @media (min-width: 1024px) {
    margin-left: 450px;
    margin-top: 227px;
  }
`;

const CardWrapper = styled.div`
  outline: none;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${colours.MIDNIGHT_BLUE};
    outline-offset: 4px;
    border-radius: 12px;
  }
`;

const iconSizes = {
	iconMobile: 32,
	iconTablet: 32,
	iconLaptop: 32,
	iconDesktop: 32,
	iconDesktopLarge: 32,
};

const ReviewModule = ({
	backgroundColour,
	removeNegativeSpacingBelowModule,
	reviewBlock,
	reviewBlockBackgroundColour,
	ctaButtonColour,
	ctaButtonLabel,
	ctaButtonUrl,
	displayStars,
	headline,
	name,
	subCopy,
}: IReviewModuleFields) => {
	const sliderRef = React.useRef<Slider>(null);
	const reviewBlockRef = React.useRef<HTMLDivElement>(null);

	const disableClonesTabIndex = React.useCallback(() => {
		if (!reviewBlockRef.current) return;
		const clonedFocusables = reviewBlockRef.current.querySelectorAll(
			".slick-cloned [tabindex], .slick-cloned a, .slick-cloned button",
		);
		for (const el of Array.from(clonedFocusables)) {
			(el as HTMLElement).setAttribute("tabindex", "-1");
		}
	}, []);

	React.useEffect(() => {
		disableClonesTabIndex();
	}, [disableClonesTabIndex]);

	const lastFocusedIndex = React.useRef<number>(-1);

	const handleCardFocus = (index: number) => {
		if (!sliderRef.current || !reviewBlockRef.current) {
			lastFocusedIndex.current = index;
			return;
		}

		const slide = reviewBlockRef.current.querySelector(
			`.slick-slide[data-index="${index}"]`,
		);
		const isVisible = slide?.classList.contains("slick-active");

		if (!isVisible && lastFocusedIndex.current !== -1) {
			if (index > lastFocusedIndex.current) {
				sliderRef.current.slickNext();
			} else {
				sliderRef.current.slickPrev();
			}
		}

		lastFocusedIndex.current = index;
	};

	function ArrowLeft(props) {
		const { className, onClick, cta_position } = props;
		return (
			<ArrowLeftSpan
				className={className}
				onClick={() => {
					mixpanelTrack("cta_arrow_click", {
						cta_position,
						cta_engagemnet: "Backward",
					});

					onClick();
				}}
				onKeyDown={onClick}
				// biome-ignore lint/a11y/useSemanticElements: <explanation>
				role="button"
				aria-label="Previous review"
			>
				<Icon
					style={{ cursor: "pointer" }}
					iconName="chevron-left"
					iconSizes={iconSizes}
					alt="Previous review"
				/>
			</ArrowLeftSpan>
		);
	}
	function ArrowRight(props) {
		const { className, onClick, cta_position } = props;
		return (
			<ArrowRightSpan
				className={className}
				onClick={() => {
					mixpanelTrack("cta_arrow_click", {
						cta_position,
						cta_engagemnet: "Forward",
					});

					onClick();
				}}
				onKeyDown={onClick}
				// biome-ignore lint/a11y/useSemanticElements: <explanation>
				role="button"
				aria-label="Next review"
			>
				<Icon
					style={{ cursor: "pointer" }}
					iconName="chevron-right"
					iconSizes={iconSizes}
					alt="Next review"
				/>
			</ArrowRightSpan>
		);
	}

	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;

	//   const headline = headline;
	const testimonialItems = reviewBlock as IModuleTestimonialItem[];

	const threeOrLessSlides = testimonialItems.length < 3;
	const numberOfSlides = testimonialItems.length;

	const paddingWhenNoCtaButton = !ctaButtonLabel;

	const settings = {
		dots: true,
		infinite: !threeOrLessSlides && true,
		speed: 500,
		slidesToShow: !threeOrLessSlides ? 3 : numberOfSlides,
		slidesToScroll: 3,
		nextArrow: !threeOrLessSlides ? (
			<ArrowRight cta_position={name} />
		) : undefined,
		prevArrow: !threeOrLessSlides ? (
			<ArrowLeft cta_position={name} />
		) : undefined,

		appendDots: (
			dots:
				| string
				| number
				| boolean
				| React.ReactFragment
				| React.ReactPortal
				| null
				| undefined,
		) => <ul>{dots}</ul>,
		customPaging: () => (
			<div className="ft-slick__dots--custom">
				{/* <div className="loading" /> */}
			</div>
		),
		responsive: [
			{
				breakpoint: 1024,
				settings: {
					centerMode: true,
					centerPadding: "40px",
					slidesToShow: 1,
					slidesToScroll: 1,
					infinite: true,
					nextArrow: <ArrowRight cta_position={name} />,
					prevArrow: <ArrowLeft cta_position={name} />,
				},
			},
		],
		afterChange: () => {
			disableClonesTabIndex();
		},
	};
	const renderAllTestimonialItems = () =>
		testimonialItems?.map(
			(testimonial, index) =>
				testimonial && (
					<CardWrapper
						key={testimonial.sys.id}
						tabIndex={0}
						aria-roledescription="slide"
						aria-label={`Review ${index + 1} of ${testimonialItems.length}${
							testimonial.fields.quoteAuthor
								? `, by ${testimonial.fields.quoteAuthor}`
								: ""
						}`}
						onFocus={() => handleCardFocus(index)}
					>
						<TestimonialItem
							quote={testimonial.fields.quote as string}
							quoteAuthor={testimonial.fields.quoteAuthor as string}
							quoteSource={testimonial.fields.quoteSource as string}
							linkToReview={testimonial.fields.linkToReview as string}
							reviewSource={testimonial.fields.reviewSource as string}
							reviewSourceLink={testimonial.fields.reviewSourceLink as string}
							reviewSourceText={testimonial.fields.reviewSourceText as string}
							displayStars={displayStars as string}
							ariaHidden={"false"}
						/>
					</CardWrapper>
				),
		);

	return (
		<Container
			resetModuleMarginBottom={resetModuleMarginBottom}
			Colour={backgroundColour}
			padding={paddingWhenNoCtaButton as boolean}
		>
			{headline && (
				<div>
					<h2>{headline}</h2>
					<p>{subCopy}</p>
				</div>
			)}
			<ReviewBlockContainer
				ref={reviewBlockRef}
				backgroundColour={reviewBlockBackgroundColour || "Peach"}
			>
				<Slider ref={sliderRef} {...settings}>
					{renderAllTestimonialItems()}
				</Slider>
			</ReviewBlockContainer>
			{ctaButtonUrl && ctaButtonColour && ctaButtonLabel && (
				<CTAButton
					url={ctaButtonUrl}
					ctaButtonColour={ctaButtonColour}
					gtmCtaPosition={name || "no position provided in contentful"}
					gtmCtaText={ctaButtonLabel}
				>
					{ctaButtonLabel}
				</CTAButton>
			)}
		</Container>
	);
};

export default ReviewModule;
